import React, { useEffect, useState } from 'react';
import {
  Modal,
  Button,
  Form,
  Table,
  Spinner,
  Alert,
  Pagination,
  Row,
  Col,
  Badge,
  ButtonGroup,
  Card,
  Image,
  ProgressBar,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import 'react-toastify/dist/ReactToastify.css';

const OwnerWallet = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [walletData, setWalletData] = useState(null);
  const [owner, setOwner] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const transactionsPerPage = 10;

  // Filter & Search
  const [filterField, setFilterField] = useState('type');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTransactions, setFilteredTransactions] = useState([]);

  // API Base URL
  const API_BASE_URL = 'https://varahibackend.varahiselfdrivecars.com/api/admin';

  // Pagination math
  const indexOfLastTransaction = currentPage * transactionsPerPage;
  const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
  const currentTransactions = filteredTransactions.slice(indexOfFirstTransaction, indexOfLastTransaction);
  const totalPages = Math.ceil(filteredTransactions.length / transactionsPerPage);

  // ---------- Helpers ----------
  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'danger';
      case 'reversed': return 'secondary';
      default: return 'secondary';
    }
  };

  const getTypeBadgeVariant = (type) => {
    switch (type) {
      case 'credit': return 'success';
      case 'withdrawal': return 'danger';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return 'fa-check-circle';
      case 'pending': return 'fa-clock';
      case 'rejected': return 'fa-times-circle';
      case 'reversed': return 'fa-undo';
      default: return 'fa-circle';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'credit': return 'fa-arrow-down';
      case 'withdrawal': return 'fa-arrow-up';
      default: return 'fa-exchange-alt';
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '₹0';
    return `₹${Number(value).toLocaleString('en-IN')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return '—';
    }
  };

  // Compute wallet usage for progress bar
  const walletUsagePercent = (() => {
    if (!walletData) return 0;
    const earned = Number(walletData.totalEarned) || 0;
    const withdrawn = Number(walletData.totalWithdrawn) || 0;
    if (earned <= 0) return 0;
    const pct = Math.min(100, Math.round((withdrawn / earned) * 100));
    return pct;
  })();

  // ---------- API ----------
  const fetchWalletData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/owner-wallet/${id}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      if (result.success) {
        setWalletData(result.wallet);
        setOwner(result.owner);
        const txs = result.transactions || [];
        setTransactions(txs);
        setFilteredTransactions(txs);
        setCurrentPage(1);
      } else {
        throw new Error(result.message || 'Failed to fetch wallet data');
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setFilteredTransactions(transactions);
      setCurrentPage(1);
      return;
    }
    const q = query.toLowerCase();
    const filtered = transactions.filter((tx) => {
      switch (filterField) {
        case 'type':
          return (tx.type || '').toLowerCase().includes(q);
        case 'status':
          return (tx.status || '').toLowerCase().includes(q);
        case 'remark':
          return (tx.remark || '').toLowerCase().includes(q);
        case 'car':
          return (tx.carId?.carName || '').toLowerCase().includes(q) ||
            (tx.carId?.vehicleNumber || '').toLowerCase().includes(q);
        default:
          return false;
      }
    });
    setFilteredTransactions(filtered);
    setCurrentPage(1);
  };

  // ---------- Pagination Renderer (1, ..., n-1, n, n+1, ..., last) ----------
  const renderPagination = () => {
    if (!totalPages || totalPages < 1) return null;
    const items = [];
    const pageSet = new Set();
    pageSet.add(1);
    pageSet.add(totalPages);
    if (currentPage > 1) pageSet.add(currentPage - 1);
    pageSet.add(currentPage);
    if (currentPage < totalPages) pageSet.add(currentPage + 1);

    const sorted = Array.from(pageSet).filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);

    let last = 0;
    sorted.forEach((page) => {
      if (page - last > 1) {
        items.push(<Pagination.Ellipsis key={`ellipsis-${page}`} disabled />);
      }
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </Pagination.Item>
      );
      last = page;
    });

    return (
      <Pagination className="mt-3 justify-content-center flex-wrap owner-wallet-pagination">
        <Pagination.First disabled={currentPage === 1} onClick={() => setCurrentPage(1)} />
        <Pagination.Prev
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        />
        {items}
        <Pagination.Next
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        />
        <Pagination.Last
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(totalPages)}
        />
      </Pagination>
    );
  };

  // ---------- Export ----------
  const exportToExcel = () => {
    try {
      if (!filteredTransactions.length) {
        toast.info('No transactions to export');
        return;
      }
      toast.info('Preparing Excel file with transactions...', { autoClose: 2000 });
      const exportData = filteredTransactions.map((tx) => ({
        'Transaction ID': tx._id,
        Type: tx.type,
        Amount: tx.amount,
        Status: tx.status,
        Remark: tx.remark || '-',
        'Booking ID': tx.bookingId?._id || '-',
        'Booking Amount': tx.bookingId?.amount ?? tx.bookingAmount ?? '-',
        'Commission %': tx.commissionPercent ?? '-',
        'Car Name': tx.carId?.carName || '-',
        'Vehicle Number': tx.carId?.vehicleNumber || '-',
        'Processed At': tx.processedAt ? new Date(tx.processedAt).toLocaleString() : '-',
        'Created At': new Date(tx.createdAt).toLocaleString(),
        'Updated At': new Date(tx.updatedAt).toLocaleString(),
      }));
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');
      worksheet['!cols'] = [
        { wch: 25 }, { wch: 12 }, { wch: 10 }, { wch: 12 },
        { wch: 25 }, { wch: 25 }, { wch: 15 }, { wch: 12 },
        { wch: 25 }, { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 20 },
      ];
      XLSX.writeFile(workbook, `OwnerWallet_${id}_${new Date().toISOString().slice(0, 19)}.xlsx`);
      toast.success('Excel file downloaded successfully!', { autoClose: 2000 });
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate Excel file');
    }
  };

  useEffect(() => {
    if (id) fetchWalletData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ---------- Wallet summary card config ----------
  const summaryCards = walletData ? [
    {
      label: 'Wallet Balance',
      value: walletData.walletBalance,
      color: walletData.walletBalance < 0 ? 'danger' : 'primary',
      icon: 'fa-wallet',
      gradient: walletData.walletBalance < 0
        ? 'linear-gradient(135deg,#ff6b6b,#c92a2a)'
        : 'linear-gradient(135deg,#4dabf7,#1864ab)',
    },
    {
      label: 'Available Balance',
      value: walletData.availableBalance,
      color: 'info',
      icon: 'fa-hand-holding-usd',
      gradient: 'linear-gradient(135deg,#22d3ee,#0e7490)',
    },
    {
      label: 'Pending Withdrawal',
      value: walletData.pendingWithdrawal,
      color: 'warning',
      icon: 'fa-hourglass-half',
      gradient: 'linear-gradient(135deg,#fbbf24,#b45309)',
    },
    {
      label: 'Total Earned',
      value: walletData.totalEarned,
      color: 'success',
      icon: 'fa-chart-line',
      gradient: 'linear-gradient(135deg,#34d399,#047857)',
    },
    {
      label: 'Total Withdrawn',
      value: walletData.totalWithdrawn,
      color: 'danger',
      icon: 'fa-money-bill-wave',
      gradient: 'linear-gradient(135deg,#f87171,#b91c1c)',
    },
    {
      label: 'Total Credits',
      value: walletData.totalCredits,
      color: 'success',
      icon: 'fa-plus-circle',
      gradient: 'linear-gradient(135deg,#4ade80,#15803d)',
    },
    {
      label: 'Total Withdrawals',
      value: walletData.totalWithdrawals,
      color: 'danger',
      icon: 'fa-minus-circle',
      gradient: 'linear-gradient(135deg,#fb7185,#9f1239)',
    },
    {
      label: 'Total Reversed',
      value: walletData.totalReversed,
      color: 'secondary',
      icon: 'fa-undo-alt',
      gradient: 'linear-gradient(135deg,#a5b4fc,#4338ca)',
    },
  ] : [];

  return (
    <div className="container-fluid p-2 p-md-3 owner-wallet-wrapper">
      <ToastContainer position="top-right" autoClose={2000} />

      {/* Inline styles for this component (scoped via class names) */}
      <style>{`
        .owner-wallet-wrapper {
          background: linear-gradient(135deg,#f8fafc 0%,#eef2ff 100%);
          min-height: 100vh;
        }
        .owner-wallet-wrapper .page-title {
          font-weight: 700;
          letter-spacing: .3px;
          background: linear-gradient(90deg,#4f46e5,#06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .summary-card {
          border: none !important;
          border-radius: 14px !important;
          color: #fff;
          transition: transform .18s ease, box-shadow .18s ease;
          overflow: hidden;
          position: relative;
        }
        .summary-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 22px rgba(0,0,0,.18);
        }
        .summary-card .summary-icon {
          font-size: 1.6rem;
          opacity: .85;
        }
        .summary-card .summary-label {
          font-size: .72rem;
          text-transform: uppercase;
          letter-spacing: .08em;
          opacity: .9;
          font-weight: 600;
        }
        .summary-card .summary-value {
          font-size: 1.35rem;
          font-weight: 700;
          margin-bottom: 0;
        }
        .owner-wallet-table thead th {
          background: linear-gradient(90deg,#1e293b,#334155) !important;
          color: #fff !important;
          font-size: .8rem;
          text-transform: uppercase;
          letter-spacing: .04em;
          vertical-align: middle;
          white-space: nowrap;
        }
        .owner-wallet-table tbody tr:hover {
          background: #f1f5ff !important;
        }
        .owner-wallet-table td {
          vertical-align: middle;
          font-size: .88rem;
        }
        .tx-amount {
          font-weight: 700;
          font-size: .95rem;
        }
        .owner-wallet-pagination .page-item.active .page-link {
          background: linear-gradient(90deg,#4f46e5,#06b6d4);
          border-color: transparent;
          color: #fff;
        }
        .owner-wallet-pagination .page-link {
          color: #4f46e5;
          border-radius: 8px !important;
          margin: 0 2px;
        }
        .owner-wallet-pagination .page-link:hover {
          background: #eef2ff;
        }
        .owner-info-card {
          border: none !important;
          border-radius: 14px !important;
          background: linear-gradient(135deg,#ffffff,#f1f5ff);
          box-shadow: 0 4px 14px rgba(15,23,42,.08);
        }
        .owner-info-card .owner-avatar {
          width: 54px; height: 54px;
          border-radius: 50%;
          background: linear-gradient(135deg,#4f46e5,#06b6d4);
          color: #fff;
          display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 1.3rem;
          letter-spacing: 1px;
        }
        .transactions-card {
          border: none !important;
          border-radius: 14px !important;
          box-shadow: 0 4px 14px rgba(15,23,42,.08);
          overflow: hidden;
        }
        .transactions-card .card-header {
          background: linear-gradient(90deg,#1e293b,#334155);
          color: #fff;
          font-weight: 600;
          border: none;
        }
        .filter-bar {
          background: #f8fafc;
          border-radius: 12px;
          padding: .75rem;
        }
        .mobile-tx-card {
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          margin-bottom: .75rem;
          background: #fff;
        }
        @media (max-width: 575.98px) {
          .summary-card .summary-value { font-size: 1.15rem; }
          .owner-wallet-wrapper { padding: .5rem !important; }
          .page-title { font-size: 1.15rem; }
        }
      `}</style>

      {/* ---------- Header ---------- */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Button
            variant="light"
            size="sm"
            className="border shadow-sm"
            onClick={() => navigate(-1)}
          >
            <i className="fas fa-arrow-left me-1"></i>Back
          </Button>
          <h3 className="mb-0 page-title">
            <i className="fas fa-wallet me-2"></i>Owner Wallet
          </h3>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" size="sm" onClick={fetchWalletData}>
            <i className="fas fa-sync-alt me-2"></i>Refresh
          </Button>
          <Button variant="success" size="sm" onClick={exportToExcel}>
            <i className="fas fa-file-excel me-2"></i>Export
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading wallet data...</p>
        </div>
      ) : error ? (
        <Alert variant="danger" className="shadow-sm">
          <i className="fas fa-exclamation-triangle me-2"></i>{error}
        </Alert>
      ) : (
        <>
          {/* ---------- Owner Info ---------- */}
          {owner && (
            <Card className="owner-info-card mb-3">
              <Card.Body>
                <Row className="align-items-center g-3">
                  <Col xs="auto">
                    <div className="owner-avatar">
                      {(owner.fullName || '?').charAt(0).toUpperCase()}
                    </div>
                  </Col>
                  <Col xs={12} md>
                    <h5 className="mb-1 fw-bold text-dark">
                      {owner.fullName}
                      <Badge bg="primary" pill className="ms-2 small align-middle">
                        <i className="fas fa-user me-1"></i>Owner
                      </Badge>
                    </h5>
                    <div className="d-flex flex-wrap gap-3 text-muted small">
                      <span><i className="fas fa-envelope me-1 text-primary"></i>{owner.email}</span>
                      <span><i className="fas fa-phone me-1 text-success"></i>{owner.mobileNumber}</span>
                      <span><i className="fas fa-fingerprint me-1 text-secondary"></i>{owner._id}</span>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* ---------- Wallet Summary ---------- */}
          {walletData && (
            <>
              {/* Wallet usage progress */}
              <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: 14 }}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    <span className="fw-semibold text-secondary small text-uppercase">
                      Withdrawal Progress
                    </span>
                    <span className="fw-bold text-primary">
                      {walletUsagePercent}% withdrawn of total earned
                    </span>
                  </div>
                  <ProgressBar
                    now={walletUsagePercent}
                    variant={
                      walletUsagePercent >= 90 ? 'danger'
                        : walletUsagePercent >= 60 ? 'warning'
                          : 'success'
                    }
                    style={{ height: 10, borderRadius: 6 }}
                    animated
                    striped
                  />
                </Card.Body>
              </Card>

              <Row className="g-3 mb-3">
                {summaryCards.map((card) => (
                  <Col xs={6} sm={6} md={4} lg={3} key={card.label}>
                    <Card
                      className="summary-card h-100"
                      style={{ background: card.gradient }}
                    >
                      <Card.Body className="d-flex flex-column justify-content-between">
                        <div className="d-flex justify-content-between align-items-start">
                          <span className="summary-label">{card.label}</span>
                          <i className={`fas ${card.icon} summary-icon`}></i>
                        </div>
                        <p className="summary-value mt-2">
                          {formatCurrency(card.value)}
                        </p>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </>
          )}

          {/* ---------- Transactions ---------- */}
          <Card className="transactions-card">
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <span>
                <i className="fas fa-history me-2"></i>
                Transaction History
                <Badge bg="light" text="dark" pill className="ms-2">
                  {filteredTransactions.length}
                </Badge>
              </span>
            </Card.Header>
            <Card.Body>
              {/* Filters */}
              <div className="filter-bar mb-3">
                <Row className="g-2">
                  <Col xs={12} md={3}>
                    <Form.Select
                      value={filterField}
                      onChange={(e) => {
                        setFilterField(e.target.value);
                        handleSearch(searchQuery);
                      }}
                    >
                      <option value="type">🔍 Search by Type</option>
                      <option value="status">🔍 Search by Status</option>
                      <option value="remark">🔍 Search by Remark</option>
                      <option value="car">🔍 Search by Car</option>
                    </Form.Select>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Control
                      type="text"
                      placeholder={`Search by ${filterField}...`}
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                  </Col>
                  <Col xs={12} md={3}>
                    <Button
                      variant="outline-secondary"
                      className="w-100"
                      onClick={() => handleSearch('')}
                    >
                      <i className="fas fa-times me-1"></i>Clear
                    </Button>
                  </Col>
                </Row>
              </div>

              {/* Desktop Table */}
              <div className="table-responsive d-none d-lg-block">
                <Table bordered hover striped className="owner-wallet-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th className="text-center">#</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Remark</th>
                      <th>Booking / Car Details</th>
                      <th>Processed At</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentTransactions.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center py-4 text-muted">
                          <i className="fas fa-inbox fa-2x d-block mb-2"></i>
                          No transactions found
                        </td>
                      </tr>
                    ) : (
                      currentTransactions.map((tx, index) => (
                        <tr key={tx._id}>
                          <td className="text-center">
                            <Badge bg="light" text="dark" pill>
                              {(currentPage - 1) * transactionsPerPage + index + 1}
                            </Badge>
                          </td>
                          <td>
                            <Badge
                              bg={getTypeBadgeVariant(tx.type)}
                              className="text-capitalize d-inline-flex align-items-center gap-1"
                            >
                              <i className={`fas ${getTypeIcon(tx.type)}`}></i>
                              {tx.type}
                            </Badge>
                          </td>
                          <td>
                            <span
                              className={`tx-amount ${tx.type === 'credit' ? 'text-success' : 'text-danger'}`}
                            >
                              {tx.type === 'credit' ? '+' : '-'}
                              {formatCurrency(tx.amount)}
                            </span>
                          </td>
                          <td>
                            <Badge
                              bg={getStatusBadgeVariant(tx.status)}
                              className="text-capitalize d-inline-flex align-items-center gap-1"
                            >
                              <i className={`fas ${getStatusIcon(tx.status)}`}></i>
                              {tx.status}
                            </Badge>
                          </td>
                          <td className="text-muted small">{tx.remark || '—'}</td>
                          <td>
                            {tx.bookingId && (
                              <div className="small">
                                <div>
                                  <i className="fas fa-calendar-check text-primary me-1"></i>
                                  <strong>Booking:</strong> {tx.bookingId._id?.slice(-8)}
                                </div>
                                <div>
                                  <i className="fas fa-rupee-sign text-success me-1"></i>
                                  <strong>Amount:</strong> {formatCurrency(tx.bookingId.amount)}
                                </div>
                                <div className="text-muted">
                                  {tx.bookingId.rentalStartDate} → {tx.bookingId.rentalEndDate}
                                </div>
                                <Badge
                                  bg={tx.bookingId.status === 'cancelled' ? 'danger' : 'info'}
                                  className="mt-1"
                                >
                                  {tx.bookingId.status}
                                </Badge>
                                {tx.commissionPercent !== undefined && (
                                  <div className="mt-1 text-muted">
                                    <i className="fas fa-percent me-1"></i>
                                    Commission: {tx.commissionPercent}%
                                  </div>
                                )}
                              </div>
                            )}
                            {tx.carId && (
                              <div className="small mt-2">
                                <div>
                                  <i className="fas fa-car text-warning me-1"></i>
                                  <strong>{tx.carId.carName}</strong>
                                </div>
                                <Badge bg="dark" className="mt-1">
                                  {tx.carId.vehicleNumber}
                                </Badge>
                              </div>
                            )}
                            {!tx.bookingId && !tx.carId && (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td className="small text-muted">
                            {tx.processedAt ? (
                              <>
                                <i className="fas fa-check-circle text-success me-1"></i>
                                {formatDate(tx.processedAt)}
                              </>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td className="small text-muted">
                            <i className="fas fa-clock me-1"></i>
                            {formatDate(tx.createdAt)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="d-lg-none">
                {currentTransactions.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="fas fa-inbox fa-2x d-block mb-2"></i>
                    No transactions found
                  </div>
                ) : (
                  currentTransactions.map((tx, index) => (
                    <Card key={tx._id} className="mobile-tx-card shadow-sm">
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="d-flex gap-2 align-items-center">
                            <Badge bg="light" text="dark" pill>
                              #{(currentPage - 1) * transactionsPerPage + index + 1}
                            </Badge>
                            <Badge
                              bg={getTypeBadgeVariant(tx.type)}
                              className="text-capitalize d-inline-flex align-items-center gap-1"
                            >
                              <i className={`fas ${getTypeIcon(tx.type)}`}></i>
                              {tx.type}
                            </Badge>
                            <Badge
                              bg={getStatusBadgeVariant(tx.status)}
                              className="text-capitalize d-inline-flex align-items-center gap-1"
                            >
                              <i className={`fas ${getStatusIcon(tx.status)}`}></i>
                              {tx.status}
                            </Badge>
                          </div>
                          <span
                            className={`tx-amount ${tx.type === 'credit' ? 'text-success' : 'text-danger'}`}
                          >
                            {tx.type === 'credit' ? '+' : '-'}
                            {formatCurrency(tx.amount)}
                          </span>
                        </div>
                        {tx.remark && (
                          <div className="small text-muted mb-2">
                            <i className="fas fa-comment-alt me-1"></i>{tx.remark}
                          </div>
                        )}
                        {tx.carId && (
                          <div className="small mb-2">
                            <i className="fas fa-car text-warning me-1"></i>
                            <strong>{tx.carId.carName}</strong>
                            <Badge bg="dark" className="ms-2">{tx.carId.vehicleNumber}</Badge>
                          </div>
                        )}
                        {tx.bookingId && (
                          <div className="small mb-2 p-2 bg-light rounded">
                            <div><strong>Booking:</strong> {tx.bookingId._id?.slice(-8)}</div>
                            <div><strong>Amount:</strong> {formatCurrency(tx.bookingId.amount)}</div>
                            <div className="text-muted">
                              {tx.bookingId.rentalStartDate} → {tx.bookingId.rentalEndDate}
                            </div>
                          </div>
                        )}
                        <div className="d-flex justify-content-between small text-muted">
                          <span>
                            <i className="fas fa-clock me-1"></i>
                            {formatDate(tx.createdAt)}
                          </span>
                          {tx.processedAt && (
                            <span>
                              <i className="fas fa-check-circle text-success me-1"></i>
                              {formatDate(tx.processedAt)}
                            </span>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  ))
                )}
              </div>

              {renderPagination()}
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
};

export default OwnerWallet;